import { ContraBidChoice, OptionsItemLiveData, SharedLocData, TableData, TextureData, VariationLocData } from '../../types/types';
import { AppAudioPlayer, SoundEffectKeys } from '../../audio/AppAudioPlayer';
import { ScreenTransitionDirection } from '../../managers/ScreenManager';
import { ScreenId } from '../../scenes/GameScene';
import { DeclarationType, PlayerDeclaration } from '../ai/DeclarationsManager';
import { PlayerSide, TableAI } from '../ai/TableAI';
import { Card, CardRank, CardSuit } from '../card/Card';
import { CardPlayer } from '../components/CardPlayer';
import { DealerPlayerToTable } from '../components/DealerPlayerToTable';
import { DealerTableToPlayer } from '../components/DealerTableToPlayer';
import { TrickCollector } from '../components/TrickCollector';
import { LoaderFileKeys } from '../constant/AppConstants';
import { OptionsDataManager } from '../managers/OptionsDataManager';
import { AppDebug, AppDebugLogType } from '../debug/AppDebug';
import { AppLocalization, LanguageId } from '../localization/AppLocalization';
import { AppLocalStorage } from '../managers/AppLocalStorage';
import { AppSettings, GamePlayDirection } from '../managers/AppSettings';
import { OptionsItemKeys } from '../managers/LayoutDataManager';
import { Player } from '../player/Player';
import { Players, SerializableAvatarProps } from '../player/Players';
import { ContraType, LayoutTypeNTAT, PopupBiddingContra } from '../popup/PopupBiddingContra';
import { PopupBiddingChoiceType } from '../popup/PopupBiddingTakeSuit';
import { PopupBinary, PopupBinaryChoiceType } from '../popup/PopupBinary';
import { DealCompleteContentId } from '../screen/dealcomplete/DealCompleteContentScroll';
import { ContractResult, DealCompleteDealsContent } from '../screen/dealcomplete/DealCompleteDealsContent';
import { DealCompleteHandsContent } from '../screen/dealcomplete/DealCompleteHandsContent';
import { DealCompleteResultsContent } from '../screen/dealcomplete/DealCompleteResultsContent';
import { DealCompleteTricksContent } from '../screen/dealcomplete/DealCompleteTricksContent';
import { GameCompleteViewLayout } from '../screen/dealcomplete/GameCompleteView';
import { ResultsListItemBubbleColor, ResultsListItemData } from '../screen/dealcomplete/ResultsListItem';
import { DealCompleteScreenExitType } from '../screen/DealCompleteScreen';
import { TableScreen, VariationId } from '../screen/TableScreen';
import { ButtonBubble } from '../ui/ButtonBubble';
import { GetCardSuitIconTextureStatesData, TrumpIconType } from '../utils/GetCardSuitIconTextureData';
import { GetCardsValue } from '../utils/GetCardsValue';
import { GetContractResult } from '../utils/GetContractResult';
import { GetOppositePlayerSide } from '../utils/GetOppositePlayerSide';
import { GetPlayerSide } from '../utils/GetPlayerSide';
import { TrumpSuitToCardSuit } from '../utils/TrumpsSuitToCardSuit';
import { TablePhase, TableStateMachine, TrumpSuit } from './TableStateMachine';
import { AppTimings } from '../data/AppConstants';

export enum TaskTypeCoinche {
	DEAL_TABLE_TO_PLAYER,
	DEAL_PLAYER_TO_TABLE,
	MAKE_BID,
	/**
	 * Give choice to attacker to RECONTRA or PASS a CONTRA
	 * - at this point this.data.contraPlayerIndex has been set to player who COINCHED
	 */
	CHOICE_ATTACKER_RECONTRA,
	RESOLVE_PLAY_CARD,

	/**
	 * Trick cards will be collected
	 * Off the table (into CardsManager)
	 */
	COLLECT_CARDS,

	/**
	 * Show Deal complete popup, let user to CONTINUE to new Deal .. (or something else?)
	 */
	COMPLETE_DEAL,
}

export enum TaskSubTypeCoinche {
	/**
	 * Default state (?)
	 */
	UNDEFINED,

	/**
	 * Dealing cards from Table to all Players
	 * - can not be interrupted and does not fork
	 * - full 'round'
	 * - before second round has been dealt
	 * - deals fixed amount of cards (3)
	 */
	FIRST_ROUND_PLAYER_DEALT,

	/**
	 * Dealing cards from Table to all Players
	 * - can not be interrupted and does not fork
	 * - full 'round'
	 * - before faceup card has been picked
	 * - deals fixed amount of cards (2)
	 */
	SECOND_ROUND_PLAYER_DEALT,

	/**
	 * Deals cards from Table to all Players
	 * - can not be interrupted and does not fork
	 * - full 'round'
	 * - after faceup card has been picked
	 * - deals fixed amount of cards (but 1 less to the player that took the faceup card) (2|3)
	 */
	THIRD_ROUND_PLAYER_DEALT,
	PLAY_PHASE,
}

export class TableStateMachineCoinche extends TableStateMachine {
	private FIRST_ROUND_CARDS_TO_PLAYER: number = 3;
	private SECOND_ROUND_CARDS_TO_PLAYER: number = 2;
	private THIRD_ROUND_CARDS_TO_PLAYER: number = 3;

	public override Init(table: TableScreen): void {
		// > table
		super.Init(table);
		// overrides
		this.variationId = VariationId.COINCHE;
	}

	public override GetDataNewGame(variationId: VariationId): TableData {
		const data: TableData = super.GetDataNewGame(variationId);

		// subclass override
		data.state = {
			// for TaskStart, TaskComplete
			taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
			// for TaskComplete
			taskSubType: TaskSubTypeCoinche.FIRST_ROUND_PLAYER_DEALT,
			taskData: {
				playerIndex: data.lastPlayedPlayerIndex,
				cardsCount: this.FIRST_ROUND_CARDS_TO_PLAYER,
			},
		};

		return data;
	}

	public override SetDataNextDeal(): void {
		super.SetDataNextDeal(false); // save?

		this.data.state = {
			taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
			taskSubType: TaskSubTypeCoinche.FIRST_ROUND_PLAYER_DEALT,
			taskData: {
				playerIndex: this.data.lastPlayedPlayerIndex,
				cardsCount: this.FIRST_ROUND_CARDS_TO_PLAYER,
			},
		};

		AppLocalStorage.SetTableData(this.variationId, this.data);
	}

	public override UpdateViewFromData(): void {
		super.UpdateViewFromData();

		// console.log(TaskTypeCoinche[this.data.state.taskType], TaskSubTypeCoinche[this.data.state.taskSubType]);
		if (this.GetTablePhase() === TablePhase.BIDDING) {
			// console.log(
			//     this.data.lastPlayedPlayerIndex,
			//     this.data.trickDealerIndex,
			//     this.data.bids.history.slice(),
			// );
			this.ReplayShowPlayerBubbles();
		}
	}

	public override TaskStart(taskType: TaskTypeCoinche, taskData: any = undefined): void {
		// console.log(`TaskStart; taskType=${TaskTypeCoinche[taskType]}, taskData=`,taskData);

		const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
		switch (taskType) {
			case TaskTypeCoinche.DEAL_TABLE_TO_PLAYER:
				const dealerTableToPlayer: DealerTableToPlayer = this.table.components.GetComponent(this.table, DealerTableToPlayer);
				dealerTableToPlayer.SetData({
					taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
					onComplete: this.OnTaskComplete.bind(this),
					playerIndex: taskData.playerIndex,
					cardsCount: taskData.cardsCount,
				});
				dealerTableToPlayer.Deal();
				break;

			// Player about to make a BID
			// This can come as result of prev operation:
			// - BID (value bid)
			// - PASS (after PASS, CONTRA, BID)
			case TaskTypeCoinche.MAKE_BID:
				// exists any value bid
				const existsBid: boolean = this.data.bids.highestChoicePlayerIndex != -1;
				// playerSide of existing highest value bid (if any)
				const highestBidPlayerSide = !existsBid ? PlayerSide.NONE : GetPlayerSide(this.data.bids.highestChoicePlayerIndex);
				// const curPlayerSide: PlayerSide = GetPlayerSide(this.data.lastPlayedPlayerIndex);
				// determine data for popupChoinceBid,
				// set that data
				// show bid popup for player to make bid choice
				const disabledChoices: PopupBiddingChoiceType[] = [];
				// disable NT, if NT A) doesnt exist or B) exists, but is disabled in options
				const enableNTAT: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
					OptionsItemKeys.enableNTAT,
					this.table.variationId
				);
				const isEnableNTAT: boolean = enableNTAT && (enableNTAT.value as boolean);
				if (!isEnableNTAT) {
					disabledChoices.push(PopupBiddingChoiceType.NOTRUMPS);
					disabledChoices.push(PopupBiddingChoiceType.ALLTRUMPS);
				}
				// CONTRA/RECONTRA button interactivity
				// highest bid does not exist, or we made it
				if (highestBidPlayerSide != PlayerSide.THEM) {
					// disable contra/recontra button
					disabledChoices.push(PopupBiddingChoiceType.CONTRA_RECONTRA);
				}
				// this porovides:
				// live player: hint text only.
				// bot: bid choice result (saved inside TableAI.lastBidChoice).
				this.table.GetTableAI().ResolveMakeBidChoice(disabledChoices);
				// if live player about to make bid
				if (this.data.lastPlayedPlayerIndex == Players.PLAYER_INDEX) {
					const enableGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
						OptionsItemKeys.enableGameTips,
						this.table.variationId
					);
					const isEnableGameTips: boolean = enableGameTips && (enableGameTips.value as boolean);
					const autoShowGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
						OptionsItemKeys.autoShowGameTips,
						this.table.variationId
					);
					const isAutoShowGameTips: boolean = autoShowGameTips && (autoShowGameTips.value as boolean);

					// CONTRA/RECONTRA button label
					let contraType: ContraType;
					// if there is a highest bid and it has been made by the OPPOSITE SIDE
					if (highestBidPlayerSide == PlayerSide.THEM) {
						// if there is CONTRA
						if (this.data.contraPlayerIndex != -1) {
							// set label to RECONTRA
							contraType = ContraType.RECONTRA;
						}
						// if there is NO CONTRA
						else {
							// set label to CONTRA
							contraType = ContraType.CONTRA;
						}
					}
					// highest bid on OUR side
					else {
						// set label to CONTRA and DISABLE button
						contraType = ContraType.CONTRA;
					}
					// bidPopup slider choices
					let choices: string[] = AppLocalization.GetVariationData().table.bidPopup.slider.choices.slice();
					const allowGenerale: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
						OptionsItemKeys.allowGenerale,
						this.table.variationId
					);
					const isAllowGenerale: boolean = allowGenerale && (allowGenerale.value as boolean);
					// further modify slider choices
					if (!isAllowGenerale) {
						const generaleChoiceIndex: number = AppLocalization.GetVariationData().table.bidPopup.slider.generale.choiceIndex;
						choices = choices.slice(); // shallow copy
						Phaser.Utils.Array.RemoveAt(choices, generaleChoiceIndex); // modifies in place
					}
					const popupContraBid: PopupBiddingContra = this.table.popupContraBid;
					// set Contra/Recontra btn label
					popupContraBid.SetContraType(contraType);
					popupContraBid.SetLayoutNTAT(
						isEnableNTAT ? LayoutTypeNTAT.YES_NTAT : LayoutTypeNTAT.NO_NTAT // NTAT layout
					);
					popupContraBid.slider.SetChoices(choices);
					popupContraBid.slider.SetStartChoiceIndex(this.data.bids.highestChoiceValueIndex + 1);
					popupContraBid.slider.SetChoiceIndex(this.data.bids.highestChoiceValueIndex + 1);
					popupContraBid.UpdateView();
					popupContraBid.SetData({
						taskType: TaskTypeCoinche.MAKE_BID,
						onComplete: this.OnTaskComplete.bind(this),
						disabledChoices: disabledChoices,
					});
					// console.log("SetContraType: ",ContraType[contraType]);
					// > SetInteractive
					popupContraBid.Show();

					if (isEnableGameTips) {
						// set hint text
						this.table.popupHint.SetText(this.table.GetTableAI().GetHintText());
						// toggle on/off tips button
						this.table.btnHints.SetSelected(isAutoShowGameTips);
						// enable tips button
						this.table.btnHints.SetInteractive(true);
						if (isAutoShowGameTips) {
							// show hints
							this.table.popupHint.Show();
						}
					}
				}
				// else AI about to make a Bid
				else {
					this.table.GetTableAI().TaskResolveMakeBid({
						taskType: TaskTypeCoinche.MAKE_BID,
						onComplete: this.OnTaskComplete.bind(this),
						disabledChoices: disabledChoices,
					});
				}
				break;

			// this.data.contraPlayerIndex just COINCHEd attacker (attacker is player on opposite side WITH HIGHEST BID so far).
			// Give chance to attacker to SURCOINCHE or PASS.
			// Either way, it's straightway to PLAY phase after that (no more bidding steps)
			case TaskTypeCoinche.CHOICE_ATTACKER_RECONTRA:
				// console.log("TaskStart: CHOICE_ATTACKER_RECONTRA");

				// if attacker is live Player
				// then show coinchePopup
				if (this.data.bids.highestChoicePlayerIndex == Players.PLAYER_INDEX) {
					const popupContra: PopupBinary = this.table.popupContra as PopupBinary;
					const playerAvatarProps: SerializableAvatarProps = this.data.playersAvatarsProps[this.data.contraPlayerIndex];
					const tableLabelSuffix = AppLocalization.GetVariationData().table.recontraPopup.title.itemLabelSuffix;
					popupContra.SetTitle(`${playerAvatarProps.name} ${tableLabelSuffix}`);
					popupContra.SetPrimaryButtonLabel(AppLocalization.GetVariationData().table.recontraPopup.primaryBtn.itemLabel);
					popupContra.SetSecondaryButtonLabel(AppLocalization.GetVariationData().table.recontraPopup.secondaryBtn.itemLabel);
					popupContra.UpdateView();
					popupContra.SetData({
						taskType: TaskTypeCoinche.CHOICE_ATTACKER_RECONTRA,
						onComplete: this.OnTaskComplete.bind(this),
					});
					popupContra.Show();
				}
				// else AI deciding to RECONTRA or PASS
				else {
					this.table.GetTableAI().TaskResolveRecontraChoice({
						taskType: TaskTypeCoinche.CHOICE_ATTACKER_RECONTRA,
						onComplete: this.OnTaskComplete.bind(this),
					});
				}
				break;

			case TaskTypeCoinche.RESOLVE_PLAY_CARD:
				// console.log("TaskStart: RESOLVE_PLAY_CARD");
				const playerIndex: number = this.data.lastPlayedPlayerIndex;
				const firstPlayerInRound: boolean = playerIndex == this.data.trickDealerIndex;
				const hasFullHand = this.table.players.GetPlayerByIndex(playerIndex).GetCards().length == 8;
				// just before first player plays card, compute all players' declarations
				if (hasFullHand && firstPlayerInRound) {
					// cache declarations (for later)
					const declarations: PlayerDeclaration[] = this.table.declarationsManager.ResolvePlayersDeclarations();
					// console.log(declarations);
					this.data.declarations = declarations;
				}
				// player deciding which card to play
				if (playerIndex == Players.PLAYER_INDEX) {
					// resolve playableCards
					// resolve suggestedCards
					// set Hint text
					const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
					this.table.GetTableAI().ResolvePlayableAndSuggestedCards(playerIndex);
					const playableCards: Card[] = this.table.GetTableAI().GetPlayableCards();
					const suggestedCards: Card[] = this.table.GetTableAI().GetSuggestedCards();
					// console.log(playableCards.map(card=>card.log()));
					// console.log(suggestedCards.map(card=>card.log()));
					const autoPlay: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
						OptionsItemKeys.autoPlay,
						this.table.variationId
					);
					const isAutoPlay: boolean = autoPlay && (autoPlay.value as boolean);
					// if autoplay last card in live player's hands
					if (isAutoPlay && playableCards.length == 1) {
						// play cards task directly
						// NOTE: we do not RESTORE POINT here,
						//       because we cant save card:Card
						this.TaskStart(TaskTypeCoinche.DEAL_PLAYER_TO_TABLE, {
							card: playableCards[0],
						});
					}
					// else play manually
					else {
						// get ref
						const popupPlayableCards: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
							OptionsItemKeys.popupPlayableCards,
							this.table.variationId
						);
						const isPopupPlayableCards: boolean = popupPlayableCards && (popupPlayableCards.value as boolean);
						const enableGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
							OptionsItemKeys.enableGameTips,
							this.table.variationId
						);
						const isEnableGameTips: boolean = enableGameTips && (enableGameTips.value as boolean);
						const autoShowGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
							OptionsItemKeys.autoShowGameTips,
							this.table.variationId
						);
						const isAutoShowGameTips: boolean = autoShowGameTips && (autoShowGameTips.value as boolean);
						const cardPlayer: CardPlayer = this.table.components.GetComponent(this.table, CardPlayer);

						// player choosing play card
						// schedule a wakeup animation (focus animation) to let player know its their turn
						this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).ScheduleWakeupAnimation();

						cardPlayer.SetData({
							taskType: TaskTypeCoinche.RESOLVE_PLAY_CARD,
							onComplete: this.OnTaskComplete.bind(this),
							playerIndex: playerIndex,
						});

						// cards interactivity
						cardPlayer.UpdateCardInteractivity(playableCards);

						// popup playable
						if (isPopupPlayableCards) {
							cardPlayer.PopPlayable(playableCards);
						}
						// update hints position (regardless of popup playable)
						this.table.popupHint.OnResize();

						if (isEnableGameTips) {
							// set hint text, show hide hints
							this.table.popupHint.SetText(this.table.GetTableAI().GetHintText());
							// toggle on/off tips button
							this.table.btnHints.SetSelected(isAutoShowGameTips);
							// enable tips button
							this.table.btnHints.SetInteractive(true);
							// auto show tips
							if (isAutoShowGameTips) {
								// hint toggle button ON = show highlights
								cardPlayer.HighlightCards(suggestedCards, playerIndex);
								// show hints
								this.table.popupHint.Show();
							} else {
								// hint toggle button OFF = no highlights
								cardPlayer.HighlightCards(player.GetCards(), playerIndex);
							}
						} else {
							// toggle on/off tips button
							this.table.btnHints.SetSelected(false);
							// enable tips button
							this.table.btnHints.SetInteractive(false);
							// hint toggle button DISABLED = no highlights
							cardPlayer.HighlightCards(player.GetCards(), playerIndex);
						}
					}
				}
				// else AI deciding which card to play
				else {
					this.table.GetTableAI().TaskResolvePlayCard({
						taskType: TaskTypeCoinche.RESOLVE_PLAY_CARD,
						onComplete: this.OnTaskComplete.bind(this),
						playerIndex: playerIndex,
					});
				}
				break;

			case TaskTypeCoinche.DEAL_PLAYER_TO_TABLE:
				const dealerPlayerToTable: DealerPlayerToTable = this.table.components.GetComponent(this.table, DealerPlayerToTable);
				dealerPlayerToTable.SetData({
					taskType: TaskTypeCoinche.DEAL_PLAYER_TO_TABLE,
					onComplete: this.OnTaskComplete.bind(this),
					playerIndex: this.data.lastPlayedPlayerIndex,
					cards: [taskData.card],
				});
				dealerPlayerToTable.Deal();
				break;

			case TaskTypeCoinche.COLLECT_CARDS:
				this.StartCollectCards(TaskTypeCoinche.COLLECT_CARDS);
				break;

			case TaskTypeCoinche.COMPLETE_DEAL:
				this.StartDealComplete(TaskTypeCoinche.COMPLETE_DEAL);
				break;

			default:
				console.warn('TaskStart: Unrecognized taskType:', TaskTypeCoinche[taskType]);
		}
	}

	public override OnTaskComplete(taskType: TaskTypeCoinche, taskData: any = undefined): void {
		// console.log(`OnTaskComplete; taskType=${TaskTypeCoinche[taskType]}, taskSubType=${TaskSubTypeCoinche[this.data.state.taskSubType]}, taskData=`,taskData);

		const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
		let lastPlayerIndex: number;
		let nextPlayedPlayerIndex: number;
		switch (taskType) {
			case TaskTypeCoinche.DEAL_TABLE_TO_PLAYER:
				// NOTE: table to player ALWAYS changes card amount in: tableStackInit, players hands
				// post-task caching:
				// - update initStackCards
				this.data.initStackCards = this.table.tableStackInit.GetCardDatas();
				// - update playersCardsData
				this.data.playersCardsData = this.table.players.GetPlayersCardDatas();

				// update state vars
				lastPlayerIndex = this.data.lastPlayedPlayerIndex;
				nextPlayedPlayerIndex = this.GetFixedPlayerIndex(lastPlayerIndex + playDirection);
				this.data.lastPlayedPlayerIndex = nextPlayedPlayerIndex;

				// One of the players was just dealt card(s).
				// - this is first round of dealing cards.
				if (this.data.state.taskSubType == TaskSubTypeCoinche.FIRST_ROUND_PLAYER_DEALT) {
					// not made whole round yet
					if (this.data.trickDealerIndex != lastPlayerIndex) {
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
							taskSubType: TaskSubTypeCoinche.FIRST_ROUND_PLAYER_DEALT,
							taskData: {
								playerIndex: nextPlayedPlayerIndex,
								cardsCount: this.FIRST_ROUND_CARDS_TO_PLAYER,
							},
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// run task
						this.TaskStart(this.data.state.taskType, {
							playerIndex: this.data.state.taskData.playerIndex,
							cardsCount: this.data.state.taskData.cardsCount,
						});
					}
					// dealer dealt to itself, round complete
					else {
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
							taskSubType: TaskSubTypeCoinche.SECOND_ROUND_PLAYER_DEALT,
							taskData: {
								playerIndex: nextPlayedPlayerIndex,
								cardsCount: this.SECOND_ROUND_CARDS_TO_PLAYER,
							},
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// run task
						this.TaskStart(this.data.state.taskType, {
							playerIndex: this.data.state.taskData.playerIndex,
							cardsCount: this.data.state.taskData.cardsCount,
						});
					}
				}
				// One of the players was just dealt card(s).
				// - this is second round of dealing cards.
				else if (this.data.state.taskSubType == TaskSubTypeCoinche.SECOND_ROUND_PLAYER_DEALT) {
					// not made whole round yet
					if (this.data.trickDealerIndex != lastPlayerIndex) {
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
							taskSubType: TaskSubTypeCoinche.SECOND_ROUND_PLAYER_DEALT,
							taskData: {
								playerIndex: nextPlayedPlayerIndex,
								cardsCount: this.SECOND_ROUND_CARDS_TO_PLAYER,
							},
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// run task
						this.TaskStart(this.data.state.taskType, {
							playerIndex: this.data.state.taskData.playerIndex,
							cardsCount: this.data.state.taskData.cardsCount,
						});
					}
					// dealer dealt to itself, round complete
					else {
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
							taskSubType: TaskSubTypeCoinche.THIRD_ROUND_PLAYER_DEALT,
							// new
							taskData: {
								playerIndex: nextPlayedPlayerIndex,
								cardsCount: this.THIRD_ROUND_CARDS_TO_PLAYER,
							},
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// run task
						this.TaskStart(this.data.state.taskType, {
							playerIndex: this.data.state.taskData.playerIndex,
							cardsCount: this.data.state.taskData.cardsCount,
						});
					}
				}
				// One of the players was just dealt card(s).
				// - this is third round of dealing cards.
				else if (this.data.state.taskSubType == TaskSubTypeCoinche.THIRD_ROUND_PLAYER_DEALT) {
					// not made whole round yet
					if (this.data.trickDealerIndex != lastPlayerIndex) {
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.DEAL_TABLE_TO_PLAYER,
							taskSubType: TaskSubTypeCoinche.THIRD_ROUND_PLAYER_DEALT,
							taskData: {
								playerIndex: nextPlayedPlayerIndex,
								cardsCount: this.THIRD_ROUND_CARDS_TO_PLAYER,
							},
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// run task
						this.TaskStart(this.data.state.taskType, {
							playerIndex: this.data.state.taskData.playerIndex,
							cardsCount: this.data.state.taskData.cardsCount,
						});
					}
					// dealer dealt to itself, round complete
					else {
						// update state vars
						// this.data.trickDealerIndex = nextPlayedPlayerIndex;
						// historyHandsData (unique single store per Deal)
						this.data.historyHandsData = this.table.players.GetPlayersCardDatas();
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.MAKE_BID,
							taskSubType: TaskSubTypeCoinche.UNDEFINED,
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// run task
						this.TaskStart(this.data.state.taskType);
					}
				}
				break;

			case TaskTypeCoinche.MAKE_BID:
				if (this.data.lastPlayedPlayerIndex == Players.PLAYER_INDEX) {
					if (this.table.popupHint.IsShowing()) {
						this.table.popupHint.Hide();
					}

					// toggle off tips button
					this.table.btnHints.SetSelected(false);
					// disable tips button
					this.table.btnHints.SetInteractive(false);
					// hide tips
					this.table.popupHint.Hide();
				}
				this.ProcessPlayersBidChoice(taskData.bidChoice as ContraBidChoice);
				break;

			case TaskTypeCoinche.CHOICE_ATTACKER_RECONTRA:
				// EDIT: untested, thus temporarily commented
				// if (this.data.lastPlayedPlayerIndex == Players.PLAYER_INDEX) {
				//     if (this.table.popupHint.IsShowing()) {
				//         this.table.popupHint.Hide();
				//     }
				// }
				this.ProcessPlayerRecontraChoice(taskData.choiceType as PopupBinaryChoiceType);
				break;

			case TaskTypeCoinche.RESOLVE_PLAY_CARD:
				const playerIndex: number = this.data.lastPlayedPlayerIndex;
				// if live Player chose card to play
				if (playerIndex === Players.PLAYER_INDEX) {
					this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).ClearWakeupAnimation(true);

					// toggle off tips button
					this.table.btnHints.SetSelected(false);
					// disable tips button
					this.table.btnHints.SetInteractive(false);
					// hide tips
					this.table.popupHint.Hide();
				}

				// call play cards task directly
				// NOTE: we do not RESTORE POINT here,
				//       because we can't save card:Card
				this.TaskStart(TaskTypeCoinche.DEAL_PLAYER_TO_TABLE, {
					card: taskData.card,
				});
				break;

			case TaskTypeCoinche.DEAL_PLAYER_TO_TABLE:
				// console.log("TaskComplete: DEAL_PLAYER_TO_TABLE");
				// post-task caching:
				// - update playStackCards
				this.data.playStackCards = this.table.tableStackPlay.GetCardDatas();
				// - update playersCardsData
				this.data.playersCardsData = this.table.players.GetPlayersCardDatas();

				// deal next player until reached dealerIndex, then GO PLAY PHASE.
				lastPlayerIndex = this.data.lastPlayedPlayerIndex;
				nextPlayedPlayerIndex = this.GetFixedPlayerIndex(lastPlayerIndex + playDirection);
				this.data.lastPlayedPlayerIndex = nextPlayedPlayerIndex;
				// nextPlayedPlayerIndex == this.data.trickDealerIndex
				// 1,1,0 -> trickDealerIndex starts dealing the round, one before him is last that deals.
				// console.log(this.data.trickDealerIndex, lastPlayerIndex, nextPlayedPlayerIndex);
				// console.log("nextPlayedPlayerIndex",nextPlayedPlayerIndex,"cardsCount",cardsCount);

				// regardless of which player in trick dealt right now,
				// inject simple declarations here
				this.table.GetTableAI().ResolveMinorDeclarationsAfterPlayerPlayedCard(lastPlayerIndex, () => {
					// console.log(this.data.trickDealerIndex, nextPlayedPlayerIndex);
					// done with simple declarations
					// not made whole round yet
					if (this.data.trickDealerIndex != nextPlayedPlayerIndex) {
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.RESOLVE_PLAY_CARD,
							taskSubType: TaskSubTypeCoinche.PLAY_PHASE,
							taskData: {
								playerIndex: nextPlayedPlayerIndex,
							},
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// start task
						this.TaskStart(this.data.state.taskType, {
							playerIndex: this.data.state.taskData.playerIndex,
						});
					}
					// dealer dealt to itself, round complete (trick over)
					else {
						// automatically (or manually) collect the trick cards
						const onComplete = () => {
							// capture restore point
							this.data.state = {
								taskType: TaskTypeCoinche.COLLECT_CARDS,
								taskSubType: TaskSubTypeCoinche.PLAY_PHASE,
							};
							// save restore point immediately to localStorage
							AppLocalStorage.SetTableData(this.variationId, this.data);
							// console.log(this.data.playStackCards);
							// console.log(AppLocalStorage.GetTableData(this.variationId));

							// start task
							this.TaskStart(this.data.state.taskType);
						};
						// automatically collect trickcards, or
						// click OK to collect trick cards
						const autoCollectTricks: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
							OptionsItemKeys.autoCollectTricks,
							this.table.variationId
						);
						if (autoCollectTricks && autoCollectTricks.value) {
							onComplete();
						} else {
							const accepterBubble: ButtonBubble = this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).acceptorBubble;
							accepterBubble.SetButtonLabel(AppLocalization.GetSharedData().table.acceptPopup.btnPrimary.itemLabel);
							accepterBubble.UpdateView();
							accepterBubble.SetData({ onComplete: onComplete });
							accepterBubble.Show();
						}
					}
				});
				break;

			// collected trick cards
			// A) play next trick (4xCHOICE_PLAY_CARD + 4xDEAL_PLAYER_TO_TABLE), or
			// B) (if player's hand empty) COMPLETE_ROUND
			case TaskTypeCoinche.COLLECT_CARDS:
				this.data.playStackCards = this.table.tableStackPlay.GetCardDatas(); // always []
				const playerCardsCount = this.table.players.GetPlayerByIndex(0).GetCards().length;
				// players have on-hand cards
				// resolve 'first trick just finished' and playing of major declarations
				if (playerCardsCount > 0) {
					// if first round trick over
					const firstTrickJustFinished: boolean = playerCardsCount == 7;
					// console.log("firstTrickJustFinished",firstTrickJustFinished);
					if (firstTrickJustFinished) {
						// if got major declaration
						const majorDeclarations: PlayerDeclaration[] = this.data.declarations?.filter(
							d => d.declarationType != DeclarationType.BELOTE_REBELOTE
						);

						majorDeclarations.forEach(d=>{
								d.declarationCards.played.forEach((p,i,a)=>{
										a[i] = true;
								});
								// console.log(d.playerIndex, DeclarationType[d.declarationType], d.declarationValue, d.declarationCards.cards.map(c=>CardSuit[c.suit]+"-"+CardRank[c.rank]));
								
								this.AddDealBonusPoints(d);
						});

						// const PlayDeclaration = (d:PlayerDeclaration, onComplete)=>{
						// 	// player bubble with declaration type
						// 		const bubbleText: string = DeclarationType[d.declarationType].toString();
						// 		this.table.players.GetPlayerByIndex(d.playerIndex).Say(bubbleText);
						// 		// animate declaration cards
						// 		this.table.declarationsManager.InjectDeclarationAnimation(
						// 			d.playerIndex,
						// 			d,
						// 			// adds delay same as when after playing a card
						// 			// saves table data
						// 			// runs SM::TaskStart with supplied props
						// 			onComplete
						// 		);
						// }

						// const hasMajorDeclarations = majorDeclarations.length > 0;
						// if (hasMajorDeclarations) {
							// let onDecAnimComplete;
						// 	for (let i=0; i<majorDeclarations.length; i++){
						// 		const majorDeclaration: PlayerDeclaration = majorDeclarations[0];
						// 		// mark declaration cards as shown
						// 		for (let i = 0; i < majorDeclaration.declarationCards.played.length; i++) {
						// 			majorDeclaration.declarationCards.played[i] = true;
						// 		}
						// 		// add declaration points as bonus points
						// 		this.AddDealBonusPoints(majorDeclaration);

						// 		if(i<majorDeclarations.length-1) {
						// 			onDecAnimComplete = this.ResolvePlayCard.bind(this, TaskTypeCoinche.RESOLVE_PLAY_CARD, TaskSubTypeCoinche.PLAY_PHASE);
						// 		}
						// 		else {
						// 			onDecAnimComplete = PlayDeclaration(majorDeclarations[i], PlayDeclaration(i+1));
						// 		}
						// 		PlayDeclaration(majorDeclarations[i], PlayDeclaration(i+1));
						// 	}
						// } else {
						// 	this.ResolvePlayCard(TaskTypeCoinche.RESOLVE_PLAY_CARD, TaskSubTypeCoinche.PLAY_PHASE);
						// }
					// }
						
						this.table.declarationsManager.InjectDeclarationAnimations(
								majorDeclarations.slice(),
								this.ResolvePlayCard.bind(this,
										TaskTypeCoinche.RESOLVE_PLAY_CARD,
										TaskSubTypeCoinche.PLAY_PHASE
								)
						);
					}
					// first trick didnt just finish
					else {
						this.ResolvePlayCard(
							TaskTypeCoinche.RESOLVE_PLAY_CARD, 
							TaskSubTypeCoinche.PLAY_PHASE
						);
					}
				}
				// players have no more cards
				// this was last trick.
				// show 'Deal complete' popup
				else {
					// EDIT: we dont save this state as restore pint
					// start task
					this.TaskStart(TaskTypeCoinche.COMPLETE_DEAL);
				}
				break;

			// dealComplete popup was closed; process user choice (taskData);
			case TaskTypeCoinche.COMPLETE_DEAL:
				// in expectation of possible 'game end'
				// NOTE: we do consider both player sides (in case max score was changed mid-game)
				const pointsPerGame: number = OptionsDataManager.GetPointsPerGame();
				let pointsPerGameReached = this.data.gameTotalScoreUs >= pointsPerGame || this.data.gameTotalScoreThem >= pointsPerGame;
				const isTie: boolean = this.data.gameTotalScoreUs == this.data.gameTotalScoreThem;
				const isGameOver = pointsPerGameReached && !isTie;
				// console.log(DealCompleteScreenExitType[taskData.exitType], "isGameOver", isGameOver)

				switch (taskData.exitType) {
					case DealCompleteScreenExitType.CONTINUE:
						// deal complete, save stats
						this.SaveStatsDealComplete();
						//
						const prevDealerIndex = this.data.dealDealerIndex;
						const nextDealerIndex = this.GetFixedPlayerIndex(prevDealerIndex + playDirection);
						// hide table content
						this.table.HideContent();
						// reset table (pool cards, etc)
						this.table.Reset();
						// set stateMachine data
						this.SetDataNextDeal();
						// build table view from said data
						this.UpdateViewFromData();
						// snap chip to prev player
						this.table.dealerChipController.SetDealerIndex(prevDealerIndex);
						// fade in table contents from state
						// NOTE: OnShow also snaps chip to player with index we set previously.
						this.table.GetScene().dealCompleteScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
						this.table.GetScene().dealCompleteScreen.Hide();
						this.table.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
						this.table.Show(true, () => {
							// animate chip from prev player to next player
							this.table.dealerChipController.SnapAnimateFromTo(prevDealerIndex, nextDealerIndex, () => {
								this.Start(false, false);
							});
						});
						break;
					case DealCompleteScreenExitType.LOBBY:
						if (isGameOver) {
							// deal complete, save stats
							this.SaveStatsDealComplete();
							// game complete, save stats
							this.SaveStatsGameComplete();
							// game complete, wipe table data
							this.DeleteTableData();
						}
						this.table.scene.tweens.killAll();
						this.table.scene.time.removeAllEvents();
						this.table.scene.time.clearPendingEvents();
						AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
						AppLocalStorage.SetLastVisitedScreenId(ScreenId.LOBBY);

						this.table.GetScene().dealCompleteScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_RIGHT;
						this.table.GetScene().dealCompleteScreen.Hide();
						this.table.GetScene().lobbyScreen.transitionDirection = ScreenTransitionDirection.LEFT_TO_CENTER;
						this.table.GetScene().lobbyScreen.Show();
						break;

					case DealCompleteScreenExitType.NEW_GAME:
						// deal complete, save stats
						this.SaveStatsDealComplete();
						// deal complete, save stats
						this.SaveStatsGameComplete();
						// hide table content
						this.table.HideContent();
						// reset table (pool cards, etc)
						this.table.Reset();
						// set stateMachine data
						this.GetSetDataNewGame(this.variationId);
						// build table view from said data
						this.UpdateViewFromData();
						// fade in table contents from state
						this.table.GetScene().dealCompleteScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
						this.table.GetScene().dealCompleteScreen.Hide();
						this.table.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
						this.table.Show(true, () => {
							// start next game (Task)
							this.Start(false, false);
						});
						break;
				}
				break;

			default:
				console.warn('OnTaskComplete: Unrecognized taskType:', TaskTypeCoinche[taskType]);
		}
	}
	
	/**
	 * Player (user or AI) has chosen a {suit|alltrumps|notrumps} or PASSed
	 * @param choiceType
	 */
	protected ProcessPlayersBidChoice(bidChoice: ContraBidChoice): void {
		// console.log("ProcessPlayersBidChoice", PopupBiddingChoiceType[bidChoice.choiceType], bidChoice.choiceValueIndex);
		const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
		// let player say choiceType
		const player: Player = this.table.players.GetPlayerByIndex(this.data.lastPlayedPlayerIndex);
		// if live player
		if (this.data.lastPlayedPlayerIndex === Players.PLAYER_INDEX) {
			// set player's bidChoice inside TableAI as lastBidChoice, so that following players
			// can properly recognize live player's BidPopup's choice
			this.table.GetTableAI().SetLastBidChoice(bidChoice);
		}
		let bubbleText: string = '';
		let bubbleIcon: TextureData = null;
		const sharedLocData: SharedLocData = AppLocalization.GetSharedData();
		const variationLocData: VariationLocData = AppLocalization.GetVariationData();
		const bidValue = AppLocalization.GetVariationData(this.variationId).table.bidPopup.slider.bubbleDeclarations[
			bidChoice.choiceValueIndex
		];
		switch (bidChoice.choiceType) {
			case PopupBiddingChoiceType.SPADES:
				this.data.trumpSuit = TrumpSuit.SPADES;
				bubbleText = bidValue; // 80 .. GENERALE
				bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.SPADES, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
				break;
			case PopupBiddingChoiceType.CLUBS:
				this.data.trumpSuit = TrumpSuit.CLUBS;
				bubbleText = bidValue;
				bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.CLUBS, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
				break;
			case PopupBiddingChoiceType.DIAMONDS:
				this.data.trumpSuit = TrumpSuit.DIAMONDS;
				bubbleText = bidValue;
				bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.DIAMONDS, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
				break;
			case PopupBiddingChoiceType.HEARTS:
				this.data.trumpSuit = TrumpSuit.HEARTS;
				bubbleText = bidValue;
				bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.HEARTS, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
				break;
			case PopupBiddingChoiceType.NOTRUMPS:
				this.data.trumpSuit = TrumpSuit.NOTRUMPS;
				bubbleText = `${bidValue} ${sharedLocData.general.noTrumpsShortText}`;
				break;
			case PopupBiddingChoiceType.ALLTRUMPS:
				this.data.trumpSuit = TrumpSuit.ALLTRUMPS;
				bubbleText = `${bidValue} ${sharedLocData.general.allTrumpsShortText}`;
				break;
			case PopupBiddingChoiceType.CONTRA_RECONTRA:
				// already have contra index?
				bubbleText =
					this.data.contraPlayerIndex != -1
						? // then player just re-contra-ed
						  variationLocData.table.declarations.recontra
						: // else player just contra-ed
						  variationLocData.table.declarations.contra;
				break;
			case PopupBiddingChoiceType.PASS:
				bubbleText = sharedLocData.table.declarations.pass;
				break;
		}

		player.Say(bubbleText, bubbleIcon, true, Number.MAX_SAFE_INTEGER);

		// player made bid/call, now wait a bit before next step
		let simpleDeclarationDuration = OptionsDataManager.GetTiming(AppTimings.simpleDeclarationDuration);
		// simpleDeclarationDuration = 1000;
		this.table.scene.time.delayedCall(
			simpleDeclarationDuration,
			() => {
				// update state vars (go next player)
				const lastPlayedPlayerIndex = this.data.lastPlayedPlayerIndex;
				const nextPlayedPlayerIndex = this.GetFixedPlayerIndex(lastPlayedPlayerIndex + playDirection);
				this.data.lastPlayedPlayerIndex = nextPlayedPlayerIndex;

				// we need to deep-clone bidChoice, to properly save it.
				this.data.bids.history.push({
					playerIndex: lastPlayedPlayerIndex,
					bidChoice: {
						choiceType: bidChoice.choiceType,
						choiceValueIndex: bidChoice.choiceValueIndex,
					},
				});

				// based on what choice player MADE
				switch (bidChoice.choiceType) {
					// made value bid
					case PopupBiddingChoiceType.SPADES:
					case PopupBiddingChoiceType.CLUBS:
					case PopupBiddingChoiceType.DIAMONDS:
					case PopupBiddingChoiceType.HEARTS:
					case PopupBiddingChoiceType.NOTRUMPS:
					case PopupBiddingChoiceType.ALLTRUMPS:
						// new bid was made (with higher value than previous)
						// this becomes the new 'attacker'
						this.data.playerAttackerIndex = lastPlayedPlayerIndex;
						// this choice also RESETS 'full round' counter, having to make at least 1 more full round of PASSes for PLAY phase to start, or an OVERBID (higher value suit BID).
						this.data.trickDealerIndex = lastPlayedPlayerIndex;
						// value bid. set data.
						this.data.bids.highestChoiceType = this.data.trumpSuit;
						this.data.bids.highestChoicePlayerIndex = lastPlayedPlayerIndex;
						this.data.bids.highestChoiceValueIndex = bidChoice.choiceValueIndex;
						// console.log('SetTableData:',
						//     PopupBiddingChoiceType[bidChoice.choiceType],
						//     TrumpSuit[this.data.bids.highestChoiceType],
						//     this.data.bids.highestChoicePlayerIndex,
						//     this.data.bids.highestChoiceValueIndex,
						// );
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.MAKE_BID,
							taskSubType: TaskSubTypeCoinche.UNDEFINED,
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// start task
						this.TaskStart(this.data.state.taskType);
						break;

					// CONTRAed highestChoiceValueIndex (there had to be one, else UI disabled)
					case PopupBiddingChoiceType.CONTRA_RECONTRA:
						// just CONTRA-ed (COINCHEd; true) OR RE-CONTRA-ed (SURCOINCHEd; false)
						const justContraed: boolean = this.data.contraPlayerIndex == -1;
						if (justContraed) {
							// const allowReContra: boolean = (OptionsDataManager.GetSharedData().itemsById[OptionsItemKeys.allowSurCoinche].value as boolean);
							const allowReContra: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
								OptionsItemKeys.allowSurCoinche,
								this.table.variationId
							);
							if (allowReContra && allowReContra.value) {
								// this choice also RESETS 'full round' counter, having to make at least 1 more full round of PASSes or a SURCOINCHE for PLAY phase to start.
								this.data.trickDealerIndex = lastPlayedPlayerIndex;
								// cache index of player that COINCHed
								this.data.contraPlayerIndex = lastPlayedPlayerIndex;
								// give chance to attacker to SURCOINCHE
								this.data.state = {
									taskType: TaskTypeCoinche.CHOICE_ATTACKER_RECONTRA,
									taskSubType: TaskSubTypeCoinche.UNDEFINED,
								};
								// save restore point immediately to localStorage
								AppLocalStorage.SetTableData(this.variationId, this.data);
								// start task
								this.TaskStart(this.data.state.taskType);
							}
							// contra was called, but recontra is disallowed to be called.
							else {
								// console.log("recontra disabled in Options.");
								// cache index of player that COINCHed
								this.data.contraPlayerIndex = lastPlayedPlayerIndex;
								// start PLAY phase
								// first make sure that next player after dealDealerIndex starts playing first card
								this.data.lastPlayedPlayerIndex = this.GetFixedPlayerIndex(this.data.dealDealerIndex + playDirection);
								// fix trickDealerIndex for play phase (needed for lastTrickView set cards)
								this.data.trickDealerIndex = this.data.lastPlayedPlayerIndex;

								// set trump view and card score view
								// EDIT: this.data.trumpSuit is always defined at this point
								// if (this.data.trumpSuit != TrumpSuit.UNDEFINED) {
								// player name
								this.table.selectedTrumpView.SetPlayerIndex(this.data.bids.highestChoicePlayerIndex);
								// trump suit
								this.table.selectedTrumpView.SetTrumpSuit(this.data.trumpSuit);
								// bid (value)
								const sBid =
									AppLocalization.GetVariationData().table.bidPopup.slider.choicesShortText[
										this.data.bids.highestChoiceValueIndex
									];
								this.table.selectedTrumpView.SetBidValue(sBid);
								// let sContra = AppLocalization.GetVariationData().dealComplete.results.itemsById["contra"].choices[0];
								let sContra: string = null;
								if (this.data.contraPlayerIndex != -1) {
									sContra = '2x';
								}
								this.table.selectedTrumpView.SetContra(sContra);

								// selectedTrumpView
								if (!this.table.selectedTrumpView.IsShowing()) {
									this.table.selectedTrumpView.Show();
								}

								const cardDeckIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardDeck).value as number;
								this.table.cardPointsView.SetData(this.data.trumpSuit, cardDeckIndex);

								if (!this.table.cardPointsView.IsShowing()) {
									this.table.cardPointsView.Show();
								}

								// update scoreView and selectedTrumpView (size, layout/snapping)
								// from screen scale OR visibility change
								this.table.UpdateScoreAndSelectedTrumpViews();
								// }

								// go PLAY phase
								// hide all bubbles (they were up with infinity duration)
								this.table.players.HideBubbles(true);

								// give choice which hand card to play
								// capture restore point
								this.data.state = {
									taskType: TaskTypeCoinche.RESOLVE_PLAY_CARD,
									taskSubType: TaskSubTypeCoinche.PLAY_PHASE,
								};
								// save restore point immediately to localStorage
								AppLocalStorage.SetTableData(this.variationId, this.data);
								// start task
								this.TaskStart(this.data.state.taskType);
							}
						} else {
							console.log('RECONTRA: handle this inside OnTaskComplete:CONTRA_RECONTRA (not here in MAKE_BID');
						}
						break;

					case PopupBiddingChoiceType.PASS:
						// console.log("PASS. this.data.trickDealerIndex:",this.data.trickDealerIndex,"lastPlayedPlayerIndex:",lastPlayedPlayerIndex, "nextPlayedPlayerIndex:",nextPlayedPlayerIndex);

						// not made whole round yet
						if (this.data.trickDealerIndex != lastPlayedPlayerIndex) {
							// if noone coinched yet, and
							// if there is attacker, and
							// if next player is attacker
							if (this.data.contraPlayerIndex == -1 && nextPlayedPlayerIndex == this.data.playerAttackerIndex) {
								// then it means that everyone after attacker Bid PASSed (3xPASS)
								// start PLAY phase
								// first make sure that next player after dealDealerIndex starts playing first card
								this.data.lastPlayedPlayerIndex = this.GetFixedPlayerIndex(this.data.dealDealerIndex + playDirection);
								// fix trickDealerIndex for play phase (needed for lastTrickView set cards)
								this.data.trickDealerIndex = this.data.lastPlayedPlayerIndex;
								// set trump view and card score view
								if (this.data.trumpSuit != TrumpSuit.UNDEFINED) {
									// update selectedTrumpView component
									// this.table.selectedTrumpView.SetPlayerIndex(this.data.lastPlayedPlayerIndex);
									// player name
									this.table.selectedTrumpView.SetPlayerIndex(this.data.bids.highestChoicePlayerIndex);
									// trump suit
									this.table.selectedTrumpView.SetTrumpSuit(this.data.trumpSuit);
									// bid (value)
									const sBid =
										AppLocalization.GetVariationData().table.bidPopup.slider.choicesShortText[
											this.data.bids.highestChoiceValueIndex
										];
									this.table.selectedTrumpView.SetBidValue(sBid); // updateLayout?
									// no contra here
									this.table.selectedTrumpView.SetContra(null);

									// selectedTrumpView
									if (!this.table.selectedTrumpView.IsShowing()) {
										this.table.selectedTrumpView.Show();
									}

									const cardDeckIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardDeck)
										.value as number;
									this.table.cardPointsView.SetData(this.data.trumpSuit, cardDeckIndex);

									if (!this.table.cardPointsView.IsShowing()) {
										this.table.cardPointsView.Show();
									}

									// update scoreView and selectedTrumpView (size, layout/snapping)
									// from screen scale OR visibility change
									this.table.UpdateScoreAndSelectedTrumpViews();
								} else {
									console.log('TrumpSuit undefined, this should NOT happen');
								}

								// hide all bubbles (they were up with infinity duration)
								this.table.players.HideBubbles(true);

								// go PLAY phase
								// give choice which hand card to play
								// capture restore point
								this.data.state = {
									taskType: TaskTypeCoinche.RESOLVE_PLAY_CARD,
									taskSubType: TaskSubTypeCoinche.PLAY_PHASE,
								};
								// save restore point immediately to localStorage
								AppLocalStorage.SetTableData(this.variationId, this.data);
								// start task
								this.TaskStart(this.data.state.taskType);
							}
							// else as usual go next BID
							else {
								// capture restore point
								this.data.state = {
									taskType: TaskTypeCoinche.MAKE_BID,
									taskSubType: TaskSubTypeCoinche.UNDEFINED,
								};
								// save restore point immediately to localStorage
								AppLocalStorage.SetTableData(this.variationId, this.data);
								// start task
								// - let next player choose suit
								this.TaskStart(this.data.state.taskType);
							}
						}
						// dealer dealt to itself, round complete
						// All players PASSed this round
						else {
							// fade out table
							this.table.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
							this.table.Hide(
								true,
								() => {
									// hide table content
									this.table.HideContent();
									// reset table (pool cards, etc)
									this.table.Reset();
									this.SetDataNextDeal();
									this.UpdateViewFromData();
									// fade in table
									this.table.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
									this.table.Show(
										true,
										() => {
											// start next table Deal (Task)
											this.Start(false, false);
										},
										(t: number) => Phaser.Math.Easing.Elastic.Out(t, 0.8, 1.2)
									);
								},
								(t: number) => Phaser.Math.Easing.Elastic.In(t, 0.8, 1.2)
							);
						}
						break;
				}
			},
			[],
			this
		);
	}

	/**
	 * Player or Bot chose recontra or PASS via @see PopupBinary
	 * @param bidChoice
	 */
	protected ProcessPlayerRecontraChoice(bidChoiceType: PopupBinaryChoiceType): void {
		// console.log("ProcessPlayerRecontraChoice", PopupBiddingChoiceType[choiceType]);

		const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
		const player: Player = this.table.players.GetPlayerByIndex(this.data.lastPlayedPlayerIndex);
		let bubbleText: string = '';
		let bubbleIcon: TextureData = null;
		const sharedLocData: SharedLocData = AppLocalization.GetSharedData();
		const variationLocData: VariationLocData = AppLocalization.GetVariationData();
		switch (bidChoiceType) {
			// Player chose RECONTRA
			case PopupBinaryChoiceType.PRIMARY:
				bubbleText = variationLocData.table.declarations.recontra;
				break;
			// Player chose PASS
			case PopupBinaryChoiceType.SECONDARY:
				bubbleText = sharedLocData.table.declarations.pass;
				break;
		}

		player.Say(bubbleText, bubbleIcon, true, Number.MAX_SAFE_INTEGER);

		// player made bid/call, now wait a bit before next step
		let simpleDeclarationDuration = OptionsDataManager.GetTiming(AppTimings.simpleDeclarationDuration);
		// simpleDeclarationDuration = 1000;
		this.table.scene.time.delayedCall(
			simpleDeclarationDuration,
			() => {
				// based on what choice player MADE
				let sContra: string = null;
				if (this.data.contraPlayerIndex != -1) {
					sContra = '2x';
				}

				switch (bidChoiceType) {
					// Player RECONTRAed
					case PopupBinaryChoiceType.PRIMARY:
						this.data.recontraPlayerIndex = this.data.lastPlayedPlayerIndex;
						sContra = '4x';

					// Player PASSed
					case PopupBinaryChoiceType.SECONDARY:
						// shared code: RECONTRA + PASS
						// start PLAY phase
						// first make sure that next player after dealDealerIndex starts playing first card
						this.data.lastPlayedPlayerIndex = this.GetFixedPlayerIndex(this.data.dealDealerIndex + playDirection);
						// fix trickDealerIndex for play phase (needed for lastTrickView set cards)
						this.data.trickDealerIndex = this.data.lastPlayedPlayerIndex;
						// set trump view and card score view
						if (this.data.trumpSuit != TrumpSuit.UNDEFINED) {
							// player name
							this.table.selectedTrumpView.SetPlayerIndex(this.data.bids.highestChoicePlayerIndex);
							// trump suit
							this.table.selectedTrumpView.SetTrumpSuit(this.data.trumpSuit);
							// bid (value)
							const sBid =
								AppLocalization.GetVariationData().table.bidPopup.slider.choicesShortText[
									this.data.bids.highestChoiceValueIndex
								];
							this.table.selectedTrumpView.SetBidValue(sBid);
							this.table.selectedTrumpView.SetContra(sContra);

							// selectedTrumpView
							if (!this.table.selectedTrumpView.IsShowing()) {
								this.table.selectedTrumpView.Show();
							}

							const cardDeckIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardDeck).value as number;
							this.table.cardPointsView.SetData(this.data.trumpSuit, cardDeckIndex);

							if (!this.table.cardPointsView.IsShowing()) {
								this.table.cardPointsView.Show();
							}

							// update scoreView and selectedTrumpView (size, layout/snapping)
							// from screen scale OR visibility change
							this.table.UpdateScoreAndSelectedTrumpViews();
						}

						// hide all bubbles (they were up with infinity duration)
						this.table.players.HideBubbles(true);

						// go PLAY phase
						// give choice which hand card to play
						// capture restore point
						this.data.state = {
							taskType: TaskTypeCoinche.RESOLVE_PLAY_CARD,
							taskSubType: TaskSubTypeCoinche.PLAY_PHASE,
						};
						// save restore point immediately to localStorage
						AppLocalStorage.SetTableData(this.variationId, this.data);
						// start task
						this.TaskStart(this.data.state.taskType);
						break;
				}
			},
			[],
			this
		);
	}

	public override GetTablePhase(): TablePhase {
		const taskType: TaskTypeCoinche = this.data.state.taskType;
		switch (taskType) {
			case TaskTypeCoinche.CHOICE_ATTACKER_RECONTRA:
			case TaskTypeCoinche.DEAL_TABLE_TO_PLAYER:
			case TaskTypeCoinche.MAKE_BID:
				return TablePhase.BIDDING;
		}
		return TablePhase.PLAY;
	}

	protected ReplayShowPlayerBubbles(): void {
		// console.log("ReplayShowPlayerBubbles");

		// DEBUG all players bubbles
		// let bt = "80";
		// let bi = GetCardSuitIconTextureStatesData(CardSuit.SPADES, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
		// // bi = null;
		// this.table.players.GetPlayerByIndex(0).Say(bt, bi, false, Number.MAX_SAFE_INTEGER);
		// this.table.players.GetPlayerByIndex(1).Say(bt, bi, false, Number.MAX_SAFE_INTEGER);
		// this.table.players.GetPlayerByIndex(2).Say(bt, bi, false, Number.MAX_SAFE_INTEGER);
		// this.table.players.GetPlayerByIndex(3).Say(bt, bi, false, Number.MAX_SAFE_INTEGER);
		// return;

		const sharedLocData: SharedLocData = AppLocalization.GetSharedData();
		const variationLocData: VariationLocData = AppLocalization.GetVariationData();
		const bubbleCount: number = Math.min(this.data.bids.history.length, 4);
		for (let i: number = 0; i < bubbleCount; i++) {
			const historyItem = this.data.bids.history[this.data.bids.history.length - 1 - i];
			const player: Player = this.table.players.GetPlayerByIndex(historyItem.playerIndex);
			let bubbleText: string = '';
			let bubbleIcon: TextureData = null;
			const bidValue = AppLocalization.GetVariationData(this.variationId).table.bidPopup.slider.bubbleDeclarations[
				historyItem.bidChoice.choiceValueIndex
			];
			switch (historyItem.bidChoice.choiceType) {
				case PopupBiddingChoiceType.SPADES:
					this.data.trumpSuit = TrumpSuit.SPADES;
					// playerSays = `${bidValue} - ${sharedLocData.table.declarations.spades}`;
					bubbleText = bidValue;
					bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.SPADES, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
					break;
				case PopupBiddingChoiceType.CLUBS:
					this.data.trumpSuit = TrumpSuit.CLUBS;
					bubbleText = bidValue;
					bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.CLUBS, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
					break;
				case PopupBiddingChoiceType.DIAMONDS:
					this.data.trumpSuit = TrumpSuit.DIAMONDS;
					bubbleText = bidValue;
					bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.DIAMONDS, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
					break;
				case PopupBiddingChoiceType.HEARTS:
					this.data.trumpSuit = TrumpSuit.HEARTS;
					bubbleText = bidValue;
					bubbleIcon = GetCardSuitIconTextureStatesData(CardSuit.HEARTS, TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT).out;
					break;
				case PopupBiddingChoiceType.NOTRUMPS:
					this.data.trumpSuit = TrumpSuit.NOTRUMPS;
					bubbleText = `${bidValue} ${sharedLocData.general.noTrumpsShortText}`;
					break;
				case PopupBiddingChoiceType.ALLTRUMPS:
					this.data.trumpSuit = TrumpSuit.ALLTRUMPS;
					bubbleText = `${bidValue} ${sharedLocData.general.allTrumpsShortText}`;
					break;
				case PopupBiddingChoiceType.CONTRA_RECONTRA:
					// already have contra index?
					bubbleText =
						this.data.contraPlayerIndex != -1
							? // then player just re-contra-ed
							  variationLocData.table.declarations.recontra
							: // else player just contra-ed
							  variationLocData.table.declarations.contra;
					break;
				case PopupBiddingChoiceType.PASS:
					bubbleText = sharedLocData.table.declarations.pass;
					break;
			}
			player.Say(bubbleText, bubbleIcon, false, Number.MAX_SAFE_INTEGER);
		}
	}
}
